import clsx from 'clsx';
import React, { useState } from 'react';
import { toggleSwitch } from '../../reducers/switch';
import Button from '../../components/Button';
import { toggleModal } from '../../reducers/modalToggle';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import SidebarCrossIcon from '../../assets/icons/sidebar-cross-icon.svg?react';
import EditIcon from '../../assets/icons/edit-icon.svg?react';
import { useDispatch, useSelector } from 'react-redux';
import PlusIcon from '../../assets/icons/add-icon.svg?react';
import ModalComponent from '../../components/ModalComponent';
import HelpActiveIcon from '../../assets/icons/help-active-icon.svg?react';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';
import ManageCustomCategoriesModal from '../../components/ManageCustomCategoriesModal';
import {
  CreateInvestigationQuestionDto,
  CreateInvestigationQuestionResponse,
  GeneralSuccessResponse,
  GetAllInvestigationQuestionCategoryOfOrganizationResponse,
  GetAllInvestigationQuestionOfOrganizationResponse,
  InvestigationQuestion,
  InvestigationQuestionCategoryWithNoOfQuestions,
  InvestigationQuestionTypeEnum,
} from '@EHSNavigator/backend';
import { useButtonSizeSmall } from '../../utils/buttonSizes';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { getAddNewQuestionSchema } from './schema';
import AddNewQuestionModal from '../../components/AddNewQuestionModal';
import { useApiClients } from '../../client/api/useApiClient';
import { useMutation } from '@tanstack/react-query';
import { queryClient } from '../../main';
import DeleteCustomQuestionModal from '../../components/DeleteCustomQuestionModal';
interface CustomQuestionsProps {
  otherClasses?: string;
  allInvestigationQuestionCategories: GetAllInvestigationQuestionCategoryOfOrganizationResponse;
  allInvestigationQuestions: GetAllInvestigationQuestionOfOrganizationResponse;
  categoriesRefetch: any;
}

const CustomQuestions: React.FC<CustomQuestionsProps> = ({
  otherClasses,
  allInvestigationQuestionCategories,
  allInvestigationQuestions,
  categoriesRefetch,
}) => {
  const customQuestionsClasses = clsx(otherClasses);

  const [state, setState] = useState<{
    deleteCategory: InvestigationQuestionCategoryWithNoOfQuestions | null;
    selectedQuestion: InvestigationQuestion | null;
    deleteQuestion: InvestigationQuestion | null;
  }>({
    deleteCategory: null,
    selectedQuestion: null,
    deleteQuestion: null,
  });

  const dispatch = useDispatch();
  const modalName = useSelector(
    (state: { modalToggle: { modalName: string } }) =>
      state.modalToggle.modalName,
  );
  const md = useButtonSizeSmall();
  const { InvestigationQuestionApi } = useApiClients();

  const addNewQuestionFormHandler = useFormik({
    enableReinitialize: true,
    initialValues: {
      question: state?.selectedQuestion?.question || '',
      questionType: state?.selectedQuestion?.type || '',
      questionCategory:
        state?.selectedQuestion?.investigationQuestionCategory?.id || '',
      options: state?.selectedQuestion?.options || [],
    },
    validationSchema: getAddNewQuestionSchema(),
    onSubmit: async (values) => {
      try {
        let res: any;
        let refinedObj: CreateInvestigationQuestionDto;

        if (
          values?.questionType === InvestigationQuestionTypeEnum?.MultiOptions
        ) {
          refinedObj = {
            question: values?.question,
            questionCategoryId: values?.questionCategory,
            questionType: values?.questionType as InvestigationQuestionTypeEnum,
            options: values?.options,
          };
        } else {
          refinedObj = {
            question: values?.question,
            questionCategoryId: values?.questionCategory,
            questionType: values?.questionType as InvestigationQuestionTypeEnum,
          };
        }
        if (state?.selectedQuestion) {
          res =
            await InvestigationQuestionApi.investigationQuestionControllerUpdate(
              {
                id: state?.selectedQuestion?.id,
                updateInvestigationQuestionDto: refinedObj,
              },
            );
          queryClient.setQueryData(
            ['all-investigation-questions'],
            (oldData: any) => {
              const updatedInvestigationQuestions = {
                ...oldData,
                investigationQuestions: oldData?.investigationQuestions?.map(
                  (node: InvestigationQuestion) =>
                    node?.id === state?.selectedQuestion?.id
                      ? res?.data?.investigationQuestion
                      : node,
                ),
              };
              return updatedInvestigationQuestions;
            },
          );
        } else {
          res =
            await InvestigationQuestionApi.investigationQuestionControllerCreate(
              {
                createInvestigationQuestionDto: refinedObj,
              },
            );
          queryClient.setQueryData(
            ['all-investigation-questions'],
            (oldData: any) => {
              const updatedInvestigationQuestions = {
                ...oldData,
                investigationQuestions: [
                  res?.data?.investigationQuestion,
                  ...(oldData?.investigationQuestions || []),
                ],
              };
              return updatedInvestigationQuestions;
            },
          );
        }

        toast.success(res?.data?.message);
        addNewQuestionFormHandler.resetForm();
        dispatch(toggleModal(''));
        setState((prev) => ({ ...prev, selectedQuestion: null }));
        categoriesRefetch();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const mutation = useMutation<CreateInvestigationQuestionResponse, Error, any>(
    {
      mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
        const response =
          await InvestigationQuestionApi.investigationQuestionControllerUpdate({
            id,
            updateInvestigationQuestionDto: {
              active,
            },
          });

        return response.data;
      },
      onSuccess: (data, { id, active }) => {
        queryClient.setQueryData(
          ['all-investigation-questions'],
          (oldData: any) => {
            const updatedInvestigationQuestions = {
              ...oldData,
              investigationQuestions: oldData?.investigationQuestions?.map(
                (node: InvestigationQuestion) =>
                  node.id === id ? { ...node, active } : node,
              ),
            };
            return updatedInvestigationQuestions;
          },
        );
        toast.success(data?.message);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      },
    },
  );

  const deleteQuestionMutation = useMutation<
    GeneralSuccessResponse,
    Error,
    any
  >({
    mutationFn: async ({ id }: { id: string }) => {
      const response =
        await InvestigationQuestionApi?.investigationQuestionControllerRemove({
          id,
        });

      return response.data;
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(
        ['all-investigation-questions'],
        (oldData: any) => {
          const updatedInvestigationQuestions = {
            ...oldData,
            investigationQuestions: oldData?.investigationQuestions?.filter(
              (node: InvestigationQuestion) => node?.id !== id,
            ),
          };
          return updatedInvestigationQuestions;
        },
      );
      toast.success(data?.message);
      dispatch(toggleModal(''));
      setState((prev) => ({ ...prev, deleteQuestion: null }));
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });

  return (
    <div className={customQuestionsClasses} data-testid="custom-questions">
      <div className="flex items-start gap-2.5">
        <button
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div className="flex w-full flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
          <div>
            <h1 className="h3">Organizational Questions (Investigation)</h1>
            <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
              Add custom questions for the injury/illness investigation tool
            </p>
          </div>
          <Button
            size="big"
            otherClasses="hidden lg:flex whitespace-nowrap bg-brand-fair-orange hover:text-brand-fair-orange hover:border-brand-fair-orange"
            variant="primary"
            onClick={() => dispatch(toggleModal('manage-custom-categories'))}
          >
            <EditIcon className="h-4 w-4 md:h-6 md:w-6 [&>path]:fill-current" />
            Manage Categories
          </Button>
        </div>
      </div>
      <p className="font-inter-variable text-neutral-tones-text/80 -tracking-text-area mt-15 mb-15 text-lg leading-5 font-medium lg:hidden">
        Add custom questions for the injury/illness investigation tool
      </p>
      <Button
        size="smaller"
        otherClasses="lg:hidden whitespace-nowrap bg-brand-fair-orange hover:text-brand-fair-orange hover:border-brand-fair-orange"
        variant="primary"
        onClick={() => dispatch(toggleModal('manage-custom-categories'))}
      >
        <EditIcon className="h-4 w-4 md:h-8 md:w-8 [&>path]:fill-current" />
        Manage Categories
      </Button>
      <hr className="border-neutral-tones-text/50 my-6 lg:my-12" />

      <div className="rounded-10 bg-card-backgrounds/5 border-neutral-tones-text/10 border px-6 py-15">
        {allInvestigationQuestions?.investigationQuestions?.length > 0 ? (
          <div>
            <div className="border-b-neutral-tones-text/35 mb-15 grid grid-cols-12 gap-5 border-b pb-15">
              <h6 className="text-neutral-tones-text font-inter-variable col-span-12 text-xl leading-6 font-bold md:col-span-4">
                Question
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable hidden text-xl leading-6 font-bold md:col-span-3 md:block">
                Type
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable hidden text-xl leading-6 font-bold md:col-span-2 md:block">
                Active
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable hidden text-xl leading-6 font-bold md:col-span-3 md:block">
                Category
              </h6>
            </div>
            {allInvestigationQuestions?.investigationQuestions?.map((node) => (
              <div>
                <div className="border-b-neutral-tones-text/10 mb-15 hidden grid-cols-12 items-center gap-5 border-b pb-15 md:grid">
                  <p className="text-neutral-tones-text font-inter-variable col-span-4 text-lg leading-5 font-normal">
                    {node?.question}
                  </p>
                  <p className="text-neutral-tones-text font-inter-variable col-span-3 text-lg leading-5 font-normal">
                    {node?.type}
                  </p>
                  <button
                    onClick={() =>
                      mutation?.mutate({
                        id: node?.id,
                        active: !node?.active,
                      })
                    }
                    disabled={
                      mutation?.isPending &&
                      mutation?.variables?.id === node?.id
                    }
                    className={clsx(
                      'relative col-span-2 h-6 w-11 cursor-pointer rounded-full border transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-60',
                      node?.active
                        ? 'bg-brand-byzantine border-transparent'
                        : 'bg-card-backgrounds/5 border-neutral-tones-text/10',
                    )}
                  >
                    <span
                      className={clsx(
                        'global-transition absolute top-2/4 right-auto z-10 block h-4 w-4 -translate-y-2/4 rounded-full',
                        node?.active
                          ? 'left-6 bg-white'
                          : 'bg-brand-byzantine left-1',
                      )}
                    ></span>
                  </button>
                  <div className="col-span-3 flex items-center justify-between">
                    <p className="text-neutral-tones-text font-inter-variable truncate text-lg leading-5 font-normal">
                      {node?.investigationQuestionCategory?.name}
                    </p>
                    <div className="flex items-center gap-15">
                      <button
                        onClick={() => {
                          setState((prev: any) => ({
                            ...prev,
                            selectedQuestion: node,
                          }));
                          dispatch(toggleModal('add-new-question'));
                        }}
                        className="cursor-pointer"
                      >
                        <EditIcon className="[&>path]:fill-brand-byzantine h-5 w-5" />
                      </button>
                      <button
                        onClick={() => {
                          setState((prev) => ({
                            ...prev,
                            deleteQuestion: node,
                          }));
                          dispatch(toggleModal('delete-question'));
                        }}
                        className="group cursor-pointer"
                      >
                        <SidebarCrossIcon className="[&>g>path]:fill-neutral-tones-text/50 group-hover:[&>g>path]:fill-brand-posh-pink h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="border-b-neutral-tones-text/10 relative z-[1] mb-15 flex items-center justify-between gap-6 border-b pb-15 md:hidden">
                  <div className="flex flex-col gap-2">
                    <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                      {node?.question}
                    </p>
                    <p className="leading-4">
                      <span className="text-brand-fair-orange font-inter-variable text-xs leading-4 font-bold">
                        Type
                      </span>
                      ・
                      <span className="text-brand-papery font-inter-variable text-xs leading-4 font-bold">
                        {node?.type}
                      </span>
                    </p>
                    <div className="text-neutral-tones-text flex items-center gap-1 leading-4">
                      <span className="text-brand-fair-orange font-inter-variable text-xs leading-4 font-bold">
                        Active
                      </span>
                      ・
                      <button
                        onClick={() =>
                          mutation?.mutate({
                            id: node?.id,
                            active: !node?.active,
                          })
                        }
                        disabled={
                          mutation?.isPending &&
                          mutation?.variables?.id === node?.id
                        }
                        className={clsx(
                          'relative h-4 w-9 cursor-pointer rounded-full border transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-60',
                          node?.active
                            ? 'bg-brand-byzantine border-transparent'
                            : 'bg-card-backgrounds/5 border-neutral-tones-text/10',
                        )}
                      >
                        <span
                          className={clsx(
                            'global-transition absolute top-2/4 right-auto z-10 block h-3 w-3 -translate-y-2/4 rounded-full',
                            node?.active
                              ? 'left-5 bg-white'
                              : 'bg-brand-byzantine left-1',
                          )}
                        ></span>
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-15">
                    <button
                      onClick={() => {
                        setState((prev: any) => ({
                          ...prev,
                          selectedQuestion: node,
                        }));
                        dispatch(toggleModal('add-new-question'));
                      }}
                      className="cursor-pointer"
                    >
                      <EditIcon className="[&>path]:fill-brand-byzantine h-5 w-5" />
                    </button>
                    <button
                      onClick={() => {
                        setState((prev) => ({
                          ...prev,
                          deleteQuestion: node,
                        }));
                        dispatch(toggleModal('delete-question'));
                      }}
                      className="group cursor-pointer"
                    >
                      <SidebarCrossIcon className="[&>g>path]:fill-neutral-tones-text/50 group-hover:[&>g>path]:fill-brand-posh-pink h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            <Button
              onClick={() => dispatch(toggleModal('add-new-question'))}
              size={md}
              variant="primary"
              otherClasses="w-fit"
            >
              <PlusIcon className="h-6 w-6 [&>path]:fill-current" /> Add
              Question
            </Button>
          </div>
        ) : (
          <div className="my-10 flex w-full flex-col items-center">
            <WarningIcon className="[&>g>path]:fill-brand-byzantine mb-4 h-16 w-16" />
            <h4 className="font-inter-variable text-neutral-tones-text font-bold">
              No questions found
            </h4>
            <p className="font-inter-variable text-neutral-tones-text/80 -tracking-text-area mt-15 mb-15 text-lg leading-5 font-medium">
              Add a question to get started
            </p>
            <Button
              onClick={() => dispatch(toggleModal('add-new-question'))}
              size={md}
              variant="primary"
              otherClasses="w-fit"
            >
              <PlusIcon className="h-6 w-6 [&>path]:fill-current" /> Add
              Question
            </Button>
          </div>
        )}
      </div>

      <div className="border-neutral-tones-text/10 bg-card-backgrounds/5 mt-12 flex w-full max-w-685 items-center gap-2.5 rounded-full border p-15">
        <HelpActiveIcon className="[&>path]:fill-brand-papery h-6 min-h-6 w-6 min-w-6 lg:h-9 lg:min-h-9 lg:w-9 lg:min-w-9" />
        <div className="flex flex-col gap-1">
          <p className="font-inter-variable text-neutral-tones-text text-xs leading-3 font-medium italic lg:text-sm lg:leading-4">
            Recommended
          </p>
          <p className="font-inter-variable text-neutral-tones-text text-sm leading-4 font-bold lg:text-base lg:leading-5">
            Refer to Six Sigma Diagram or Fishbone Diagram to formulate your
            questions.
          </p>
        </div>
      </div>
      {[
        'manage-custom-categories',
        'add-new-question',
        'delete-question',
      ].includes(modalName) && (
        <ModalComponent
          otherClasses="px-4 py-4"
          additionalClass={clsx(
            ' h-fit max-h-full w-full p-5 lg:px-9 lg:py-6  rounded-2xl',
            modalName === 'manage-custom-categories'
              ? state?.deleteCategory
                ? 'max-w-500'
                : 'max-w-970'
              : 'max-w-573',
          )}
          customClose={() => {
            if (
              modalName === 'manage-custom-categories' &&
              state?.deleteCategory
            ) {
              setState((prev) => ({ ...prev, deleteCategory: null }));
            } else {
              dispatch(toggleModal(''));
              setState((prev) => ({ ...prev, selectedQuestion: null }));
            }
          }}
        >
          {modalName === 'manage-custom-categories' && (
            <ManageCustomCategoriesModal
              allInvestigationQuestionCategories={
                allInvestigationQuestionCategories
              }
              handleClose={() => {
                dispatch(toggleModal(''));
              }}
              handleDeleteCategory={(e) =>
                setState((prev) => ({ ...prev, deleteCategory: e }))
              }
              deleteCategory={state?.deleteCategory}
            />
          )}
          {modalName === 'add-new-question' && (
            <AddNewQuestionModal
              updateQuestion={state?.selectedQuestion ? true : false}
              formHandler={addNewQuestionFormHandler}
              handleClose={() => {
                dispatch(toggleModal(''));
                setState((prev) => ({
                  ...prev,
                  selectedQuestion: null,
                  deleteQuestion: null,
                }));
              }}
              selectedQuestion={state?.selectedQuestion}
              allInvestigationQuestionCategories={
                allInvestigationQuestionCategories
              }
            />
          )}
          {modalName === 'delete-question' && (
            <DeleteCustomQuestionModal
              question={state?.deleteQuestion}
              handleClose={() => {
                dispatch(toggleModal(''));
                setState((prev) => ({ ...prev, deleteQuestion: null }));
              }}
              isLoading={deleteQuestionMutation.isPending}
              handleDeleteQuestion={(e) => {
                deleteQuestionMutation?.mutate({
                  id: e?.id,
                });
              }}
            />
          )}
        </ModalComponent>
      )}
    </div>
  );
};

export default CustomQuestions;
